<!-- Reusable search highlight fragment: splits text into segments, bolding the matched portion.
     Both branches must stay wrapped in an element: a bare interpolation keeps the template's
     surrounding newlines as a collapsed space, which renders as a gap beside every match. -->
<ng-template #hl let-text let-search="search">
  @for (seg of text | highlightSearch: search; track seg) {
    @if (seg.isMatch) {
      <span class="search-highlight">{{ seg.text }}</span>
    }
    @if (!seg.isMatch) {
      <span>{{ seg.text }}</span>
    }
  }
</ng-template>

<div
  class="card flex justify-content-center table-container app-resources-view"
  [class.table-loading]="loading"
  [class.table-empty]="!loading && resources?.length === 0"
  [style.--table-scroll-height]="scrollHeight"
>
  <!-- The [virtualScroll] gate counts parents only, so an app with few parents and thousands of
       expanded children renders unvirtualized. Gating on the flat render list would flip
       virtualization on and off as the user expands, which needs its own verification pass. -->
  <p-table
    #dt1
    [value]="displayRows"
    [globalFilterFields]="globalFilterFields"
    dataKey="resourceArn"
    [scrollable]="true"
    [scrollHeight]="scrollHeight"
    [tableStyle]="{ 'min-width': '30rem' }"
    [showCurrentPageReport]="true"
    class="p-datatable-sm arp-prime-datatable"
    [rowHover]="true"
    [totalRecords]="displayRows?.length ?? 0"
    [paginator]="false"
    overflow="auto"
    selectionMode="single"
    [showLoader]="false"
    [loading]="loading || _initialFilterPending"
    [rowTrackBy]="resourceTrackBy"
    [virtualScroll]="(displayRows?.length ?? 0) > virtualScrollRowThreshold"
    [virtualScrollItemSize]="45"
    [virtualScrollOptions]="virtualScrollOptions"
    (onFilter)="onTableFilter($event)"
    (onSort)="onTableSort()"
  >
    <ng-template #caption>
      <div class="table-caption">
        <!-- Tab heading (Resources h3 or Recovery/Failback subtabs) -->
        <ng-content select=".tab-heading"></ng-content>

        <div class="caption-right">
          <p-iconfield class="filter-container">
            <input
              pInputText
              #filterInput
              type="text"
              class="input-extra-small"
              (input)="filterGlobal($event)"
              placeholder="Search..."
            />
            @if (!isFilterApplied && !loadingFilterInput) {
              <p-inputicon class="fa-utility fa-semibold fa-magnifying-glass search-icon" />
            }
            @if (isFilterApplied && !loadingFilterInput) {
              <p-inputicon
                (click)="clearInput(dt1)"
                (keydown.enter)="clearInput(dt1); $event.preventDefault()"
                (keydown.space)="clearInput(dt1); $event.preventDefault()"
                class="fa-utility fa-semibold fa-xmark"
                role="button"
                tabindex="0"
                aria-label="Clear search"
              />
            }
            @if (loadingFilterInput) {
              <p-inputicon class="fa-solid fa-spinner fa-spin" />
            }
          </p-iconfield>
          <!-- Filter controls row — filter-by, refresh, and export stay on one line -->
          <div class="filter-controls-row">
            <div class="filter-by-container" #filterDropdown>
              <button
                class="filter-by-button input-extra-small"
                (click)="toggleFilterDropdown()"
                [disabled]="!resources?.length"
              >
                Filter by
                @if (!loadingFilterBy) {
                  <i
                    class="fa-utility fa-semibold caret-icon caret-icon--small"
                    [ngClass]="showFilterDropdown ? 'fa-angle-up' : 'fa-angle-down'"
                  ></i>
                }
                @if (loadingFilterBy) {
                  <i class="fa-solid fa-spinner fa-spin caret-icon caret-icon--small"></i>
                }
              </button>
              @if (showFilterDropdown) {
                <div class="filter-by-dropdown" [style.max-height]="filterDropdownMaxHeight">
                  <!-- Filter Sections (data-driven) -->
                  @for (section of _filterSections; track trackBySectionKey($index, section)) {
                    <div class="filter-section">
                      <div class="filter-section-header" (click)="toggleFilterSection(section.key)">
                        <i
                          class="fa-utility fa-semibold"
                          [ngClass]="expandedFilterSections[section.key] ? 'fa-angle-down' : 'fa-angle-right'"
                        ></i>
                        {{ section.label }}
                      </div>
                      @if (expandedFilterSections[section.key]) {
                        @if (section.key === 'resourceType' && _resourceTypeLetters.length > 5) {
                          <div class="filter-section-sort-container">
                            <div class="letter-strip-label">Sort by:</div>
                            <!-- Letter strip for resource type quick-filter -->
                            <div class="letter-strip">
                              @for (letter of _resourceTypeLetters; track letter) {
                                <button
                                  class="letter-btn"
                                  [class.active]="activeLetterFilter === letter"
                                  (click)="toggleLetterFilter(letter)"
                                >
                                  {{ letter }}
                                </button>
                              }
                            </div>
                          </div>
                        }
                        <!-- Skeleton pills while data is loading -->
                        @if (section.pills.length === 0 && loading) {
                          <div class="filter-pills">
                            @for (_ of [1, 2, 3, 4]; track _) {
                              <p-skeleton width="5rem" height="1.6rem" borderRadius="var(--input-border-radius)" />
                            }
                          </div>
                        }
                        @if (section.pills.length > 0) {
                          <div class="filter-pills">
                            @for (pill of section.pills; track trackByPillId($index, pill)) {
                              <button
                                class="filter-pill"
                                [class.active]="isPillActive(section.key, pill.id)"
                                [hidden]="!isPillVisibleForLetter(section.key, pill)"
                                (click)="onFilterPillClick(section.key, pill.id)"
                              >
                                {{ pill.label }}
                                <span class="pill-count">{{ getPillCount(section.key, pill.id) }}</span>
                              </button>
                            }
                          </div>
                        }
                      }
                    </div>
                  }
                </div>
              }
            </div>
            <!--refresh-btn from applications component html-->
            <ng-content select=".refresh-btn"></ng-content>
            <!-- Export button -->
            <button
              class="export-btn arpio-secondary-button extra-small-button"
              (click)="exportToCsv()"
              [disabled]="!resources?.length"
              pTooltip="Export to CSV"
              tooltipPosition="top"
            >
              <i class="fa-sharp fa-regular fa-arrow-up-from-bracket"></i>
            </button>
          </div>
        </div>

        <!-- Active Filter Tags (full-width row below header controls) -->
        @if (hasActiveFilters) {
          <div class="filter-tags-container">
            <!-- Status Filter Tags -->
            @for (status of displayedStatusFilters; track status) {
              <p-tag severity="info" class="filter-tag">
                <span>{{ getStatusLabel(status) }}</span>
                <span class="pill-count">{{ getStatusCount(status) }}</span>
                <i class="fa-utility fa-semibold fa-xmark filter-tag-remove" (click)="removeStatusFilter(status)"></i>
              </p-tag>
            }
            <!-- Resource Type Filter Tags -->
            @for (type of selectedResourceTypes; track type) {
              <p-tag severity="info" class="filter-tag">
                <span>{{ type }}</span>
                <span class="pill-count">{{ getResourceTypeCount(type) }}</span>
                <i
                  class="fa-utility fa-semibold fa-xmark filter-tag-remove"
                  (click)="removeResourceTypeFilter(type)"
                ></i>
              </p-tag>
            }
            <!-- Resource Group Filter Tags -->
            @for (group of selectedResourceGroups; track group) {
              <p-tag severity="info" class="filter-tag">
                <span>{{ group }}</span>
                <span class="pill-count">{{ getResourceGroupCount(group) }}</span>
                <i
                  class="fa-utility fa-semibold fa-xmark filter-tag-remove"
                  (click)="removeResourceGroupFilter(group)"
                ></i>
              </p-tag>
            }
            <!-- Issues Filter Tag -->
            @if (issuesFilterActive) {
              <p-tag severity="info" class="filter-tag">
                <span>Issues</span>
                <span class="pill-count">{{ getIssuesCount() }}</span>
                <i class="fa-utility fa-semibold fa-xmark filter-tag-remove" (click)="toggleIssuesFilter()"></i>
              </p-tag>
            }
            <!-- Search Tag -->
            @if (isFilterApplied && filterText) {
              <p-tag severity="info" class="filter-tag">
                <span>Search: "{{ filterText }}"</span>
                <i class="fa-utility fa-semibold fa-xmark filter-tag-remove" (click)="clearInput(dt1)"></i>
              </p-tag>
            }
            <!-- Clear All -->
            <button class="clear-all-filters-btn" (click)="clearAllFilters()">Clear All</button>
          </div>
        }
      </div>
    </ng-template>

    <!-- Header starts here -->
    <ng-template #header>
      <tr>
        <th pSortableColumn="status" class="status-column strong-text">
          <div class="status-text strong-text">Status <p-sort-icon field="status"></p-sort-icon></div>
        </th>

        <th pSortableColumn="displayResourceType" class="resource-type-column">
          <div class="strong-text">Resource Type <p-sort-icon field="displayResourceType"></p-sort-icon></div>
        </th>

        <!-- Headers render based on app type (Azure) and scope (backup vs recovery),
          but NOT on whether resources is empty — so the header row is stable across
          empty/populated states. Per-cell placeholder logic handles missing values. -->
        @if (shouldShowResourceGroupColumn) {
          <th pSortableColumn="sourceResourceGroup" class="table-column strong-text resource-group-column">
            Resource Group
            <p-sort-icon field="sourceResourceGroup"></p-sort-icon>
          </th>
        }
        <th pSortableColumn="sourceResourceName" class="table-column strong-text name-column">
          Primary Resource
          <p-sort-icon field="sourceResourceName"></p-sort-icon>
        </th>

        <!-- Recovery Resource column — scope-based: only appears on Recovery context. -->
        @if (isRecoveryContext) {
          <th pSortableColumn="targetResourceName" class="table-column strong-text name-column">
            Recovery Resource
            <p-sort-icon field="targetResourceName"></p-sort-icon>
          </th>
        }
      </tr>
    </ng-template>

    <ng-template #loadingbody>
      @for (_ of skeletonRows; track _) {
        <tr class="skeleton-loading-row">
          <td class="status-column">
            <p-skeleton width="5.5rem" height="1.4rem" borderRadius="var(--input-border-radius)" />
          </td>
          <td class="resource-type-column">
            <p-skeleton width="80%" height="1rem" />
          </td>
          <!-- Column visibility mirrors the header row (Azure app type + scope). -->
          @if (shouldShowResourceGroupColumn) {
            <td class="resource-group-column">
              <p-skeleton width="60%" height="1rem" />
            </td>
          }
          <td class="name-column">
            <p-skeleton width="90%" height="1rem" />
          </td>
          @if (isRecoveryContext) {
            <td class="name-column">
              <p-skeleton width="85%" height="1rem" />
            </td>
          }
        </tr>
      }
    </ng-template>

    <ng-template #emptymessage>
      <!-- While loading: render the skeleton/discovering branch. Once loading resolves and resources
        is empty (whether genuinely empty from first load or after the user removed all resources),
        fall through to emptyContent so the real empty state renders. With filters active, skip
        skeletons regardless of loading and show the empty-content "no match" message. -->
      @if (loading && !hasActiveFilters) {
        <!-- When resources are configured but not yet discovered, show an informative message instead of skeleton rows -->
        @if (resourcesConfiguredButEmpty) {
          <!-- Structural row — see note on empty-structural-row in #emptyContent. -->
          <tr class="empty-structural-row" aria-hidden="true">
            <td class="status-column"></td>
            <td class="resource-type-column"></td>
            @if (shouldShowResourceGroupColumn) {
              <td class="resource-group-column"></td>
            }
            <td class="name-column"></td>
            @if (isRecoveryContext) {
              <td class="name-column"></td>
            }
          </tr>
          <tr>
            <td [attr.colspan]="99" [style.height]="scrollHeight">
              <div class="empty-table-message">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>Your resources are being discovered. They'll appear here shortly.</span>
                <span class="empty-table-subtext"
                  >This may take a moment while we process your configured resources.</span
                >
              </div>
            </td>
          </tr>
        } @else {
          @for (_ of skeletonRows; track _) {
            <tr class="skeleton-loading-row">
              <td class="status-column">
                <p-skeleton width="5.5rem" height="1.4rem" borderRadius="var(--input-border-radius)" />
              </td>
              <td class="resource-type-column">
                <p-skeleton width="80%" height="1rem" />
              </td>
              <!-- Column visibility mirrors the header row (Azure app type + scope). -->
              @if (shouldShowResourceGroupColumn) {
                <td class="resource-group-column">
                  <p-skeleton width="60%" height="1rem" />
                </td>
              }
              <td class="name-column">
                <p-skeleton width="90%" height="1rem" />
              </td>
              @if (isRecoveryContext) {
                <td class="name-column">
                  <p-skeleton width="85%" height="1rem" />
                </td>
              }
            </tr>
          }
        }
      } @else {
        <!-- Structural row mirrors the loading/body column layout so PrimeNG computes
            column widths correctly. Without this, a single colspan=99 row makes the
            Primary Resource column collapse to ~0px since there's no per-column td
            for the browser to size against in table-layout: fixed. -->
        <tr class="empty-structural-row" aria-hidden="true">
          <td class="status-column"></td>
          <td class="resource-type-column"></td>
          @if (shouldShowResourceGroupColumn) {
            <td class="resource-group-column"></td>
          }
          <td class="name-column"></td>
          @if (isRecoveryContext) {
            <td class="name-column"></td>
          }
        </tr>
        <tr>
          <td [attr.colspan]="99" [style.height]="scrollHeight">
            @if (hasActiveFilters) {
              <div class="empty-table-message">
                <i class="fa-utility fa-semibold fa-filter"></i>
                <span>No resources match the current filters.</span>
                <button class="clear-filters-link" (click)="clearAllFilters()">Clear all filters</button>
              </div>
            } @else {
              <div class="empty-table-message">
                <i class="fa-sharp fa-regular fa-cube"></i>
                <span
                  >{{ emptyText }}
                  @if (showConfigureLink) {
                    <a [routerLink]="['configure']" fragment="application-resource-selection">Configure resources.</a>
                  }
                </span>
              </div>
            }
          </td>
        </tr>
      }
    </ng-template>

    <ng-template #body let-resource>
      <!-- Parents, their expanded children, and regular rows all arrive through PrimeNG's [value]
           as one flat list so the virtual scroller sizes and positions every row. _rowKind is
           stamped in buildDisplayRows / _syncRenderRows; the switch picks the markup. -->
      @switch (resource._rowKind) {
        <!-- Expandable parent row — a real resource row (e.g., EKS cluster) that has children.
             Shows expand toggle + faceted pill in first cell, resource name in remaining cells. -->
        @case ('parent') {
          <tr
            class="expandable-parent-row"
            (click)="toggleExpandableParent(resource.resourceArn)"
            (keydown.enter)="toggleExpandableParent(resource.resourceArn); $event.preventDefault()"
            (keydown.space)="toggleExpandableParent(resource.resourceArn); $event.preventDefault()"
            [attr.aria-expanded]="expandableGroups.get(resource.resourceArn)?.expanded"
            role="button"
            tabindex="0"
            [attr.aria-label]="resource.displayResourceType + ' group'"
          >
            <!-- Status column: caret + (Needs attention rollup pill OR parent's own status pill) + issue icon.
                    The Needs attention pill replaces the parent's own pill whenever any descendant is in
                    a trigger state — drawing the eye to problems even on rows the user might otherwise
                    filter past as a generic "Succeeded". -->
            <td class="expandable-parent-header-cell status-column">
              <div class="expandable-parent-status">
                <i
                  class="expandable-parent-caret"
                  [ngClass]="
                    expandableGroups.get(resource.resourceArn)?.loading
                      ? 'fa-solid fa-spinner fa-spin'
                      : 'fa-utility fa-semibold ' +
                        (expandableGroups.get(resource.resourceArn)?.expanded ? 'fa-angle-down' : 'fa-angle-right')
                  "
                ></i>
                <!-- "Needs attention" rollup pill replaces the parent's own status pill whenever any
                        descendant is in a trigger state (failed / blocked / not-processed / scan failures).
                        Passive indicator — the issue icon next to it is the click target for filtering. -->
                @if (resource._needsAttention) {
                  <div
                    class="needs-attention-pill"
                    [pTooltip]="expandableGroups.get(resource.resourceArn)?.needsAttentionTooltip || null"
                    tooltipPosition="top"
                  >
                    Needs attention
                  </div>
                } @else {
                  <!-- _displayStatus promotes a settled parent to PROCESSING/PENDING when descendants
                          are still in-flight (e.g. cluster reads SUCCESS while pods are still restoring).
                          When promoted, drop the progress gradient — descendant progress isn't aggregated. -->
                  <resource-status-pill
                    [status]="resource._displayStatus ?? resource.status"
                    [progress]="
                      resource._displayStatus && resource._displayStatus !== resource.status ? 0 : resource.progress
                    "
                    [context]="statusContext"
                    [substatus]="resource.substatus"
                    [clickable]="resource._isLinkable"
                    (pillClick)="onNotProcessedPillClick($event)"
                  ></resource-status-pill>
                }
                <!-- Issue icon — clickable to filter children to issues only.
                        Swaps to a spinner during the deferred filter pass (mirrors the facet pattern). -->
                @if (resource._childIssueCount > 0 || expandableGroups.get(resource.resourceArn)?.applyingIssues) {
                  <i
                    [ngClass]="
                      expandableGroups.get(resource.resourceArn)?.applyingIssues
                        ? 'fa-solid fa-spinner fa-spin expandable-parent-issue-icon'
                        : 'fa-sharp fa-solid fa-circle-exclamation expandable-parent-issue-icon expandable-parent-issue-icon--warn'
                    "
                    [pTooltip]="resource._childIssueCount + ' with issues'"
                    tooltipPosition="top"
                    (click)="filterToIssues($event, resource.resourceArn)"
                  ></i>
                }
              </div>
            </td>
            <td class="resource-type-column">
              <span class="expandable-parent-title"
                ><ng-container
                  *ngTemplateOutlet="hl; context: { $implicit: resource.displayResourceType, search: filterText }"
                ></ng-container
              ></span>
            </td>
            <!-- Remaining columns mirror header visibility (Azure + scope).
                    Primary Resource always renders so deleted-app rows still show a "Deleted" placeholder. -->
            @if (shouldShowResourceGroupColumn) {
              <td class="no-starting-icon resource-group-column">
                @if (resource.sourceResourceGroup) {
                  <span class="cell-truncatable" [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
                    ><ng-container
                      *ngTemplateOutlet="hl; context: { $implicit: resource.sourceResourceGroup, search: filterText }"
                    ></ng-container
                  ></span>
                }
              </td>
            }
            <td class="no-starting-icon">
              @if (resource.sourceResourceName) {
                <span class="cell-truncatable" [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
                  ><ng-container
                    *ngTemplateOutlet="hl; context: { $implicit: resource.sourceResourceName, search: filterText }"
                  ></ng-container
                ></span>
              } @else {
                <span
                  class="cell-truncatable cell-placeholder"
                  [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
                  >&mdash;</span
                >
              }
            </td>
            @if (isRecoveryContext) {
              <td class="no-starting-icon">
                @if (resource.targetResourceName) {
                  <span class="cell-truncatable" [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
                    ><ng-container
                      *ngTemplateOutlet="hl; context: { $implicit: resource.targetResourceName, search: filterText }"
                    ></ng-container
                  ></span>
                } @else {
                  <span
                    class="cell-truncatable cell-placeholder"
                    [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
                    >{{ _cachedRecoveryPlaceholders.get(resource.resourceArn) }}</span
                  >
                }
              </td>
            }
          </tr>
        }

        <!-- Child of an expanded parent (e.g., a K8s resource under its EKS cluster). -->
        @case ('child') {
          <tr class="expandable-child-row" (click)="toggleCellExpand(resource.resourceArn)">
            <td class="status-column-pill">
              <div class="status-with-issues">
                <resource-status-pill
                  [status]="resource.status"
                  [progress]="resource.progress"
                  [context]="statusContext"
                  [substatus]="resource.substatus"
                  [clickable]="resource._isLinkable"
                  (pillClick)="onNotProcessedPillClick($event)"
                ></resource-status-pill>
                @if (resource.hasIssues) {
                  <a
                    class="issues-indicator"
                    (click)="onIssuesClick($event, resource)"
                    [pTooltip]="_cachedIssueTooltips.get(resource.resourceArn)"
                    tooltipPosition="top"
                  >
                    <i class="fa-sharp fa-solid fa-circle-exclamation"></i>
                  </a>
                }
              </div>
            </td>
            <td class="resource-type-column">
              <span
                ><ng-container
                  *ngTemplateOutlet="hl; context: { $implicit: resource.displayResourceType, search: filterText }"
                ></ng-container
              ></span>
            </td>
            <!-- Columns mirror header visibility (Azure + scope). -->
            @if (shouldShowResourceGroupColumn) {
              <td class="no-starting-icon resource-group-column">
                <span class="cell-truncatable" [class.cell-expanded]="isCellExpanded(resource.resourceArn)">
                  @if (resource.sourceResourceGroup) {
                    <ng-container
                      *ngTemplateOutlet="hl; context: { $implicit: resource.sourceResourceGroup, search: filterText }"
                    ></ng-container>
                  } @else {
                    <span class="cell-placeholder">N/A</span>
                  }
                </span>
              </td>
            }
            <td class="no-starting-icon">
              <span
                class="cell-truncatable"
                [class.cell-placeholder]="!resource.sourceResourceName"
                [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
              >
                @if (resource.sourceResourceName) {
                  <ng-container
                    *ngTemplateOutlet="hl; context: { $implicit: resource.sourceResourceName, search: filterText }"
                  ></ng-container>
                } @else {
                  &mdash;
                }
              </span>
              @if (resource.sourceResourceName) {
                <a
                  class="resource-copy-btn"
                  [copy-clipboard]="resource.sourceResourceName"
                  (copied)="onResourceNameCopied(resource.resourceArn, 'source')"
                  pTooltip="Copy name"
                  tooltipPosition="top"
                  (click)="$event.stopPropagation()"
                >
                  <i
                    class="fa-utility fa-semibold"
                    [ngClass]="copyFeedback.copiedStates[resource._copySourceKey] ? 'fa-check' : 'fa-copy'"
                  ></i>
                </a>
              }
              @if (resource.sourceResourcePortalUri) {
                <a
                  [href]="resource.sourceResourcePortalUri"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="resource-portal-link"
                  [pTooltip]="resource._portalTooltip"
                  tooltipPosition="top"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square"></i>
                </a>
              }
            </td>
            @if (isRecoveryContext) {
              <td class="no-starting-icon">
                <span
                  class="cell-truncatable"
                  [class.cell-placeholder]="!resource.targetResourceName"
                  [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
                >
                  @if (resource.targetResourceName) {
                    <ng-container
                      *ngTemplateOutlet="hl; context: { $implicit: resource.targetResourceName, search: filterText }"
                    ></ng-container>
                  } @else {
                    {{ _cachedRecoveryPlaceholders.get(resource.resourceArn) }}
                  }
                </span>
                @if (resource.targetResourceName) {
                  <a
                    class="resource-copy-btn"
                    [copy-clipboard]="resource.targetResourceName"
                    (copied)="onResourceNameCopied(resource.resourceArn, 'target')"
                    pTooltip="Copy name"
                    tooltipPosition="top"
                    (click)="$event.stopPropagation()"
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [ngClass]="copyFeedback.copiedStates[resource._copyTargetKey] ? 'fa-check' : 'fa-copy'"
                    ></i>
                  </a>
                }
                @if (resource.targetResourcePortalUri) {
                  <a
                    [href]="resource.targetResourcePortalUri"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="resource-portal-link"
                    [pTooltip]="resource._portalTooltip"
                    tooltipPosition="top"
                  >
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </a>
                }
              </td>
            }
          </tr>
        }

        <!-- Synthetic row: shown while an expanding group sorts its children and rebuilds caches. -->
        @case ('child-loading') {
          <tr class="expandable-child-row child-notice-row">
            <td [attr.colspan]="subheadingColspan">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Loading resources...</span>
            </td>
          </tr>
        }

        <!-- Synthetic row: the group has children but the active filters narrowed them to zero.
             Lets the user bypass global filters for just this group so the body isn't empty. -->
        @case ('child-empty-filter') {
          <tr class="expandable-child-row child-notice-row empty-filter-row">
            <td [attr.colspan]="subheadingColspan">
              <span class="empty-filter-message">No children match the current filter.</span>
              <a
                class="empty-filter-link"
                (click)="toggleBypassGroupFilters(resource._parentArn); $event.stopPropagation()"
              >
                {{
                  expandableGroups.get(resource._parentArn)?.bypassGlobalFilters
                    ? 'Reapply filter'
                    : 'See children anyway'
                }}
              </a>
            </td>
          </tr>
        }

        <!-- Normal resource row (no children) — click anywhere to expand/collapse -->
        @default {
          <tr class="resource-row" (click)="onRowClick($event, resource)">
            <td class="status-column-pill" [ngClass]="{ 'divider-row': !isExpandedRow(resource.resourceArn) }">
              <div class="status-with-expand">
                @if (hasExpandableContent(resource)) {
                  <button
                    class="row-expand-btn"
                    [attr.aria-expanded]="isExpandedRow(resource.resourceArn)"
                    aria-label="Expand resource details"
                    (click)="
                      toggleRow(resource.resourceArn); toggleCellExpand(resource.resourceArn); $event.stopPropagation()
                    "
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [ngClass]="isExpandedRow(resource.resourceArn) ? 'fa-angle-down' : 'fa-angle-right'"
                    ></i>
                  </button>
                }
                <div class="status-with-issues">
                  <resource-status-pill
                    [status]="resource.status"
                    [progress]="resource.progress"
                    [context]="statusContext"
                    [substatus]="resource.substatus"
                    [clickable]="resource._isLinkable"
                    (pillClick)="onNotProcessedPillClick($event)"
                  ></resource-status-pill>
                  @if (resource.hasIssues) {
                    <a
                      class="issues-indicator"
                      (click)="onIssuesClick($event, resource)"
                      [pTooltip]="_cachedIssueTooltips.get(resource.resourceArn)"
                      tooltipPosition="top"
                    >
                      <i class="fa-sharp fa-solid fa-circle-exclamation"></i>
                    </a>
                  }
                </div>
              </div>
            </td>
            <td class="resource-type-column" [ngClass]="{ 'divider-row': !isExpandedRow(resource.resourceArn) }">
              <span
                ><ng-container
                  *ngTemplateOutlet="hl; context: { $implicit: resource.displayResourceType, search: filterText }"
                ></ng-container
              ></span>
            </td>
            <!-- Columns mirror header visibility (Azure + scope). Primary Resource always
                              renders so deleted-app rows still show a "Deleted" placeholder. -->
            @if (shouldShowResourceGroupColumn) {
              <td
                class="no-starting-icon resource-group-column"
                [ngClass]="{ 'divider-row': !isExpandedRow(resource.resourceArn) }"
              >
                <!-- Resource Group: "N/A" when the resource type doesn't use resource groups (sourceResourceGroup is null/undefined) -->
                <span class="cell-truncatable" [class.cell-expanded]="isCellExpanded(resource.resourceArn)">
                  @if (resource.sourceResourceGroup) {
                    <ng-container
                      *ngTemplateOutlet="hl; context: { $implicit: resource.sourceResourceGroup, search: filterText }"
                    ></ng-container>
                  } @else {
                    <span class="cell-placeholder">N/A</span>
                  }
                </span>
              </td>
            }
            <!-- Primary Resource (source): placeholder from getPrimaryResourcePlaceholder() when sourceResourceName is empty.
                                "Deleted" if app is deleted, "—" if source was removed from account or access revoked. -->
            <td class="no-starting-icon" [ngClass]="{ 'divider-row': !isExpandedRow(resource.resourceArn) }">
              <span
                class="cell-truncatable"
                [class.cell-placeholder]="_cachedPrimaryIsPlaceholder.get(resource.resourceArn)"
                [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
              >
                @if (!_cachedPrimaryIsPlaceholder.get(resource.resourceArn)) {
                  <ng-container
                    *ngTemplateOutlet="hl; context: { $implicit: resource.sourceResourceName, search: filterText }"
                  ></ng-container>
                } @else {
                  {{ _cachedPrimaryPlaceholderText.get(resource.resourceArn) }}
                }
              </span>
              @if (resource.sourceResourceName) {
                <a
                  class="resource-copy-btn"
                  [copy-clipboard]="resource.sourceResourceName"
                  (copied)="onResourceNameCopied(resource.resourceArn, 'source')"
                  pTooltip="Copy name"
                  tooltipPosition="top"
                  (click)="$event.stopPropagation()"
                >
                  <i
                    class="fa-utility fa-semibold"
                    [ngClass]="copyFeedback.copiedStates[resource._copySourceKey] ? 'fa-check' : 'fa-copy'"
                  ></i>
                </a>
              }
              @if (resource.sourceResourcePortalUri) {
                <a
                  [href]="resource.sourceResourcePortalUri"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="resource-portal-link"
                  [pTooltip]="resource._portalTooltip"
                  tooltipPosition="top"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square"></i>
                </a>
              }
            </td>
            <!-- Recovery Resource column — scope-based: only appears on Recovery context.
                                  Empty-cell placeholder logic: see JSDoc on getRecoveryResourcePlaceholder() in app-resources-view.component.ts -->
            @if (isRecoveryContext) {
              <td class="no-starting-icon" [ngClass]="{ 'divider-row': !isExpandedRow(resource.resourceArn) }">
                <span
                  class="cell-truncatable"
                  [class.cell-placeholder]="!resource.targetResourceName"
                  [class.cell-expanded]="isCellExpanded(resource.resourceArn)"
                >
                  @if (resource.targetResourceName) {
                    <ng-container
                      *ngTemplateOutlet="hl; context: { $implicit: resource.targetResourceName, search: filterText }"
                    ></ng-container>
                  } @else {
                    {{ _cachedRecoveryPlaceholders.get(resource.resourceArn) }}
                  }
                </span>
                @if (resource.targetResourceName) {
                  <a
                    class="resource-copy-btn"
                    [copy-clipboard]="resource.targetResourceName"
                    (copied)="onResourceNameCopied(resource.resourceArn, 'target')"
                    pTooltip="Copy name"
                    tooltipPosition="top"
                    (click)="$event.stopPropagation()"
                  >
                    <i
                      class="fa-utility fa-semibold"
                      [ngClass]="copyFeedback.copiedStates[resource._copyTargetKey] ? 'fa-check' : 'fa-copy'"
                    ></i>
                  </a>
                }
                @if (resource.targetResourcePortalUri) {
                  <a
                    [href]="resource.targetResourcePortalUri"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="resource-portal-link"
                    [pTooltip]="resource._portalTooltip"
                    tooltipPosition="top"
                  >
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </a>
                }
              </td>
            }
          </tr>

          @if (isExpandedRow(resource.resourceArn)) {
            <tr class="expanded-row">
              <td class="divider-row"></td>
              <td class="divider-row" [attr.colspan]="subheadingColspan - 1">
                @if (resource?.resourceProperties.length > 0) {
                  <!-- The real resource properties -->
                  @for (prop of resource.resourceProperties; track prop) {
                    <div class="prop-row">
                      <span class="prop prop-name">{{ prop.name }}: </span>
                      <span class="prop prop-val"
                        >{{ prop.value }}
                        <a
                          class="copy-clipboard"
                          [copy-clipboard]="prop.value"
                          [pTooltip]="prop.value"
                          tooltipPosition="top"
                        >
                          &nbsp;<i class="fa-utility fa-semibold fa-copy copy-glyph"></i>
                        </a>
                      </span>
                    </div>
                  }
                }
                @if (isS3Bucket(resource)) {
                  <div class="prop-row">
                    <!-- Synthetic property to display objectReplicationMode -->
                    <span class="prop prop-name">Replication Mode: </span>
                    <span class="prop prop-val">{{ s3ReplicationMode(resource) }}</span>
                  </div>
                }
              </td>
            </tr>
          }
        }
      }
    </ng-template>

    <ng-template #paginatorleft>
      <p-button type="button" icon="pi pi-plus" styleClass="p-button-text"></p-button>
    </ng-template>
    <ng-template #paginatorright>
      <p-button type="button" icon="pi pi-cloud" styleClass="p-button-text"></p-button>
    </ng-template>
    <ng-template #summary>
      @if (!loading && resources?.length > 0) {
        <div class="summary-content">
          <span class="summary-count">Displaying {{ displayedCount }} of {{ resources.length }} resources</span>
        </div>
      }
    </ng-template>
  </p-table>
</div>
